import styled, { css } from 'styled-components';

import {
  getUploadTypeFromPhotoTab,
  useFileUpload,
} from '../../hooks/useFileUpload';
import { ImageKey, ImageWithType } from '../../types.ts/general';
import { trackDownload } from '../../utility/unsplash';
import { PhotoTab } from '../../types.ts/story';

import PhotoIcon from '@src/svgs/PhotoIcon';
import RotatingSquare from '../../svgs/RotatingSquare';
import CircleCheckIcon from '../../svgs/CircleCheckIcon';
import SpinningLoading from '../SpinningLoading';

type Props = {
  replaceAction: () => void;
  title: string;
  isSelected: boolean;
  selectedImage?: ImageWithType[ImageKey] | null;
  origin?: string;
  isLoading?: boolean;
  addImageLabel?: string;
  addImageLoadingLabel?: string;
  acceptFormats?: string[];
  tab: PhotoTab;
  setSelectedImage?: (img: ImageWithType[ImageKey] | null) => void;
};

const PhotoModalTop = (props: Props) => {
  const isTranscriptPhoto = props.origin === 'transcript';
  const { acceptFormats, handleUploadFile, isUploading } = useFileUpload({
    acceptType: 'image',
    uploadType: getUploadTypeFromPhotoTab(props.tab),
    onUploadComplete: (artifact) => {
      const imageWithType: ImageWithType[ImageKey] = {
        id: artifact[0].id,
        url: artifact[0].url,
        type: 'artifact',
        width: artifact[0].width || '',
        height: artifact[0].height || '',
        alt: artifact[0].title || '',
      };
      props.setSelectedImage?.(imageWithType);
    },
  });

  const handleReplaceAction = () => {
    recordSelectedImageUsage();
    props.replaceAction();
  };

  const recordSelectedImageUsage = () => {
    if (props.selectedImage?.type === 'stock') {
      trackDownload(props.selectedImage.attribution?.download_location);
    }
  };

  const { addImageLabel = 'Replace', addImageLoadingLabel = 'Replacing...' } =
    props;

  const showUploadButton =
    props.tab === PhotoTab.artifact || props.tab === PhotoTab.orgArtifact;

  return (
    <>
      <Title origin={props.origin}>{props.title}</Title>
      {!isTranscriptPhoto && (
        <SubTitle>Select the image you'd like to use instead</SubTitle>
      )}
      <ButtonContainer>
        <ActionButton
          isSelected={props.isSelected}
          isTranscriptPhoto={isTranscriptPhoto}
          onClick={handleReplaceAction}
          disabled={props.isLoading}
        >
          {isTranscriptPhoto && (
            <RotatingSquare
              strokeColor={!props.isSelected ? '#484848' : undefined}
            />
          )}
          {props.isLoading ? addImageLoadingLabel : addImageLabel}
          {!isTranscriptPhoto && (
            <CircleCheckIcon
              strokeColor={!props.isSelected ? '#484848' : undefined}
            />
          )}
        </ActionButton>

        {showUploadButton && (
          <ActionButton
            isOutline
            isTranscriptPhoto={isTranscriptPhoto}
            onClick={() => window.getSelection()?.empty()}
            disabled={isUploading}
          >
            <PhotoIcon strokeColor="#17C964" />
            Upload Image
            <FileInput
              key={isUploading ? 'upload-disabled' : 'upload-enabled'}
              type="file"
              accept={acceptFormats?.join(',') || 'image/*'}
              onChange={handleUploadFile}
              disabled={isUploading}
              title=""
            />
          </ActionButton>
        )}
      </ButtonContainer>
      {isUploading && (
        <SpinningLoading
          customStyle={{ top: 0, zIndex: 1 }}
          Ico={<PhotoIcon strokeColor="#17C964" strokeWidth={2} />}
          text={'Uploading image...'}
        />
      )}
    </>
  );
};

export default PhotoModalTop;

const Title = styled.h3<{ origin?: string }>`
  color: #f3e9d7;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 10px;
  ${(props) =>
    props.origin === 'transcript' &&
    css`
      margin-top: 20px;
    `}
`;

const SubTitle = styled.h5`
  color: #f3e9d7;
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 15px;
`;

const ActionButton = styled.button<{
  isSelected?: boolean;
  isTranscriptPhoto?: boolean;
  isOutline?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: #484848;
  border: 1px solid #484848;
  border-radius: 8px;
  width: 200px;
  height: 48px;
  gap: 8px;
  background: #03041a;
  font-weight: 800;
  outline: none;
  position: relative;
  ${(props) =>
    props.isSelected &&
    css`
      background: #17c964;
      color: #03041a;
      border: 1px solid #17c964;
    `}
  ${(props) =>
    props.isOutline &&
    css`
      border: 1px solid #17c964;
      color: #17c964;
    `}
  &:disabled {
    cursor: not-allowed;
  }
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  &:disabled {
    display: none;
  }
`;
